import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '../components/Logo.js';
import { theme } from '../theme.js';

/**
 * Public front page for signed-out visitors at `/`: what MusterTap is and who
 * it's for, with screens from a demo unit. Responsive layout needs media
 * queries, so it carries its own scoped stylesheet (`lp-` classes) built from
 * the theme. Screenshots are .webp in public/landing so the service worker
 * doesn't precache them for signed-in users.
 */

const BLUE = '#2196f3';
const ORANGE = '#f47a2a';
const YELLOW = '#fbc02d';
const NAVY = '#13223f';

const OVERVIEW_VIDEO = '7xL0Mnx5uUk';
const ROLE_VIDEOS = [
  ['Team members', 'https://youtu.be/HX7uzFu_KpE'],
  ['Team leaders', 'https://youtu.be/4S8kl4NXfR4'],
  ['Unit controllers', 'https://youtu.be/1Br3Y3pYwBg'],
  ['Equipment officers', 'https://youtu.be/F19ynZ3eF38'],
];

const css = `
.lp { background: ${theme.bg}; color: ${theme.text}; font-family: ${theme.font}; font-size: 16px; line-height: 1.55; min-height: 100dvh; }
.lp h1, .lp h2, .lp h3 { line-height: 1.1; margin: 0; text-wrap: balance; letter-spacing: -0.02em; font-weight: 800; }
.lp p { margin: 0; }
.lp a:focus-visible { outline: 3px solid ${BLUE}; outline-offset: 3px; border-radius: 6px; }
.lp-wrap { max-width: 1120px; margin: 0 auto; padding-left: max(16px, env(safe-area-inset-left, 0px)); padding-right: max(16px, env(safe-area-inset-right, 0px)); }

.lp-btn { display: inline-flex; align-items: center; justify-content: center; font: 600 16px/1 ${theme.font};
  padding: 14px 22px; border-radius: 12px; text-decoration: none; border: 1px solid transparent; }
.lp-btn-primary { background: ${theme.accent}; color: ${theme.accentInk}; }
.lp-btn-primary:hover { filter: brightness(1.08); }
.lp-btn-ghost { background: ${theme.surface}; color: ${theme.text}; border-color: ${theme.borderHi}; }
.lp-btn-ghost:hover { border-color: ${theme.textMuted}; }
.lp-btn-small { padding: 10px 16px; font-size: 15px; }

.lp-hero { position: relative; overflow: hidden; }
.lp-hero::before { content: ""; position: absolute; right: -180px; top: -120px; width: 720px; height: 720px; border-radius: 50%;
  border: 18px solid rgba(33,150,243,.09); box-shadow: 0 0 0 70px ${theme.bg}, 0 0 0 88px rgba(33,150,243,.06); pointer-events: none; }
.lp-nav { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding-top: max(18px, env(safe-area-inset-top, 0px)); padding-bottom: 18px; position: relative; }
.lp-hero-grid { display: grid; grid-template-columns: 1.1fr .9fr; gap: 48px; align-items: center; padding-top: 40px; position: relative; }
.lp-hero-copy { min-width: 0; padding-bottom: 64px; }
.lp-eyebrow { display: inline-flex; align-items: center; gap: 8px; font: 600 13px/1.3 ${theme.font}; letter-spacing: .08em; text-transform: uppercase; color: ${theme.accent}; }
.lp-eyebrow::before { content: ""; width: 8px; height: 8px; border-radius: 50%; background: ${ORANGE}; flex: none; }
.lp-hero h1 { font-size: clamp(2.5rem, 6vw, 4.25rem); margin-top: 18px; }
.lp-hero h1 em { font-style: normal; color: ${BLUE}; }
.lp-lead { font-size: 1.175rem; color: ${theme.textDim}; max-width: 34em; margin-top: 20px; }
.lp-actions { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 28px; }
.lp-note { margin-top: 14px; font-size: 14px; color: ${theme.textMuted}; }
.lp-hero-phone { align-self: end; justify-self: center; width: min(360px, 100%); }

.lp-phone { background: ${NAVY}; padding: 10px 10px 0; border-radius: 34px 34px 0 0; box-shadow: 0 24px 48px rgba(19,34,63,.22); max-width: 100%; }
.lp-phone img { display: block; width: 100%; height: auto; border-radius: 24px 24px 0 0; }
.lp-phone.full { border-radius: 34px; padding: 10px; }
.lp-phone.full img { border-radius: 24px; }

.lp-strip { background: ${theme.surface}; border-block: 1px solid ${theme.border}; }
.lp-strip .lp-wrap { display: grid; grid-template-columns: repeat(3, 1fr); gap: 32px; padding-block: 36px; }
.lp-strip h3 { font-size: 1.15rem; font-weight: 600; letter-spacing: 0; display: flex; align-items: center; gap: 10px; }
.lp-strip p { color: ${theme.textDim}; font-size: 15px; margin-top: 8px; }
.lp-label { grid-column: 1 / -1; font: 600 13px/1 ${theme.font}; letter-spacing: .08em; text-transform: uppercase; color: ${theme.textMuted}; }
.lp-dot { width: 10px; height: 10px; border-radius: 50%; flex: none; }

.lp-video { padding-block: 72px; }
.lp-video h2 { font-size: clamp(2rem, 4vw, 2.75rem); margin-top: 12px; }
.lp-player { position: relative; display: block; width: 100%; max-width: 880px; aspect-ratio: 16 / 9; margin-top: 28px; padding: 0; border: 0; border-radius: 16px; overflow: hidden; background: ${NAVY}; cursor: pointer; box-shadow: 0 24px 48px rgba(19,34,63,.22); }
.lp-player:focus-visible { outline: 3px solid ${BLUE}; outline-offset: 3px; }
.lp-player img, .lp-player iframe { display: block; width: 100%; height: 100%; border: 0; }
.lp-play { position: absolute; left: 55%; top: 50%; width: clamp(44px, 9vw, 76px); height: clamp(44px, 9vw, 76px); transform: translate(-50%, -50%); border-radius: 50%; background: ${ORANGE}; color: #fff; display: grid; place-items: center; box-shadow: 0 8px 24px rgba(0,0,0,.35); }
.lp-player:hover .lp-play { filter: brightness(1.08); }
.lp .lp-video-note { margin-top: 12px; font-size: 14px; color: ${theme.textMuted}; }
.lp-roles { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; margin-top: 20px; }
.lp-roles b { font-weight: 600; margin-right: 4px; }

.lp-feature { padding-block: 88px; }
.lp-feature + .lp-feature { border-top: 1px solid ${theme.border}; }
.lp-feature-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: center; }
.lp-feature.flip .lp-feature-copy { order: 2; }
.lp-feature-copy { min-width: 0; }
.lp-kicker { font: 600 13px/1 ${theme.font}; letter-spacing: .08em; text-transform: uppercase; color: ${theme.accent}; }
.lp-feature h2 { font-size: clamp(2rem, 4vw, 2.75rem); margin-top: 12px; }
.lp-sub { font-size: 1.1rem; color: ${theme.textDim}; margin-top: 14px; max-width: 32em; }
.lp-points { list-style: none; padding: 0; margin: 28px 0 0; display: grid; gap: 18px; }
.lp-points li { display: grid; grid-template-columns: 28px 1fr; gap: 12px; }
.lp-points b { display: block; font-weight: 600; }
.lp-points span { color: ${theme.textDim}; font-size: 15px; }
.lp-tick { width: 28px; height: 28px; border-radius: 8px; background: ${theme.status.ok.bg}; color: ${theme.status.ok.fg}; display: grid; place-items: center; }
.lp-shot { justify-self: center; width: min(380px, 100%); }
.lp-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 20px; }
.lp-chip { font: 600 13px/1 ${theme.font}; padding: 6px 9px; border-radius: 7px; background: ${theme.status.muted.bg}; color: ${theme.status.muted.fg}; font-variant-numeric: tabular-nums; }
.lp-chip.ok { background: ${theme.status.ok.bg}; color: ${theme.status.ok.fg}; }

.lp-band { background: ${theme.surface}; border-block: 1px solid ${theme.border}; padding-block: 72px; }
.lp-band h2 { font-size: clamp(1.6rem, 3.2vw, 2.25rem); }
.lp-band .lp-sub { max-width: 40em; margin-top: 12px; font-size: 1rem; }
.lp-steps { list-style: none; padding: 0; margin: 36px 0 0; display: grid; grid-template-columns: repeat(5, 1fr); counter-reset: s; }
.lp-steps li { counter-increment: s; position: relative; padding-right: 18px; min-width: 0; }
.lp-steps li::before { content: counter(s); display: grid; place-items: center; width: 40px; height: 40px; border-radius: 50%;
  background: ${theme.accent}; color: ${theme.accentInk}; font: 800 18px/1 ${theme.font}; position: relative; z-index: 1; }
.lp-steps li::after { content: ""; position: absolute; left: 40px; right: 0; top: 19px; height: 2px; background: ${theme.border}; }
.lp-steps li:last-child::after { display: none; }
.lp-steps b { display: block; margin-top: 14px; font-size: 1.05rem; }
.lp-steps span { color: ${theme.textDim}; font-size: 15px; }
.lp-callout { margin-top: 32px; padding: 16px 18px; border-radius: 12px; background: ${theme.status.warn.bg}; color: ${theme.status.warn.fg}; font-size: 15px; max-width: 760px; }

.lp-who h2 { margin-top: 12px; max-width: 24em; }
.lp-who-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-top: 32px; }
.lp-who-card { min-width: 0; border-top: 3px solid ${ORANGE}; padding-top: 14px; }
.lp-who-card:nth-child(2) { border-color: ${YELLOW}; }
.lp-who-card:nth-child(3) { border-color: ${BLUE}; }
.lp-who-card:nth-child(4) { border-color: ${theme.accent}; }
.lp-who-card b { display: block; font-size: 1.05rem; }
.lp-who-card span { display: block; margin-top: 6px; color: ${theme.textDim}; font-size: 15px; }

.lp-duo { padding-block: 88px; display: grid; grid-template-columns: 1fr 1fr; gap: 56px; }
.lp-duo > div { min-width: 0; }
.lp-duo h2 { font-size: clamp(1.6rem, 3vw, 2rem); }
.lp-duo .lp-sub { font-size: 1rem; margin-top: 12px; }
.lp-devices { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-top: 24px; }
.lp-device { border: 1px solid ${theme.border}; background: ${theme.surface}; border-radius: 14px; padding: 18px 14px; text-align: center; }
.lp-device svg { color: ${theme.accent}; }
.lp-device b { display: block; margin-top: 8px; font-size: 15px; }
.lp-device span { font-size: 13px; color: ${theme.textMuted}; }

.lp-close { background: ${theme.surface}; border-top: 1px solid ${theme.border}; }
.lp-close .lp-wrap { padding-block: 64px; display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 28px; }
.lp-close h2 { font-size: clamp(1.75rem, 3.5vw, 2.5rem); }
.lp-close p { color: ${theme.textDim}; margin-top: 10px; max-width: 36em; }
.lp-footer { color: ${theme.textMuted}; font-size: 13px; }
.lp-footer a { color: inherit; }
.lp-footer .lp-wrap { padding-top: 24px; padding-bottom: max(24px, env(safe-area-inset-bottom, 0px)); display: flex; flex-wrap: wrap; justify-content: space-between; gap: 8px 24px; }

@media (max-width: 860px) {
  .lp-hero-grid, .lp-feature-grid, .lp-duo { grid-template-columns: 1fr; gap: 40px; }
  .lp-hero-copy { padding-bottom: 0; }
  .lp-hero-phone { width: min(320px, 88%); }
  .lp-feature.flip .lp-feature-copy { order: 0; }
  .lp-feature, .lp-duo { padding-block: 64px; }
  .lp-band { padding-block: 56px; }
  .lp-strip .lp-wrap { grid-template-columns: 1fr; gap: 20px; }
  .lp-who-grid { grid-template-columns: 1fr 1fr; gap: 24px 16px; }
  .lp-steps { grid-template-columns: 1fr; gap: 22px; }
  .lp-steps li { display: grid; grid-template-columns: 40px 1fr; column-gap: 14px; padding: 0; }
  .lp-steps li::before { grid-row: span 2; }
  .lp-steps li::after { left: 19px; right: auto; top: 40px; bottom: -22px; width: 2px; height: auto; }
  .lp-steps b { margin-top: 8px; }
}
@media (max-width: 480px) {
  .lp-who-grid, .lp-devices { grid-template-columns: 1fr; }
}
@media (prefers-reduced-motion: no-preference) {
  .lp-hero-phone { animation: lp-rise .7s cubic-bezier(.2,.7,.2,1) both; }
  @keyframes lp-rise { from { transform: translateY(24px); } to { transform: none; } }
}
`;

function Tick() {
  return (
    <span className="lp-tick" aria-hidden="true">
      <svg width="16" height="16" viewBox="0 0 16 16">
        <path d="M3 8.5l3 3 7-7" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

function Points({ items }: { items: [string, string][] }) {
  return (
    <ul className="lp-points">
      {items.map(([title, body]) => (
        <li key={title}><Tick /><div><b>{title}</b><span>{body}</span></div></li>
      ))}
    </ul>
  );
}

function Shot({ src, alt, w, h, eager = false }: { src: string; alt: string; w: number; h: number; eager?: boolean }) {
  return <img src={src} alt={alt} width={w} height={h} loading={eager ? 'eager' : 'lazy'} />;
}

/** Loads nothing from YouTube until the visitor presses play. */
function VideoPlayer({ id, title, poster }: { id: string; title: string; poster: string }) {
  const [playing, setPlaying] = useState(false);
  if (playing) {
    return (
      <div className="lp-player">
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`}
          title={title}
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </div>
    );
  }
  return (
    <button type="button" className="lp-player" onClick={() => setPlaying(true)} aria-label={`Play video: ${title}`}>
      <img src={poster} alt="" width={1280} height={720} loading="lazy" />
      <span className="lp-play" aria-hidden="true">
        <svg width="32" height="32" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" fill="currentColor" /></svg>
      </span>
    </button>
  );
}

const DEVICE_ICON = { width: 30, height: 30, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, 'aria-hidden': true };

export function LandingPage() {
  return (
    <div className="lp">
      <style>{css}</style>

      <header className="lp-hero">
        <div className="lp-wrap">
          <nav className="lp-nav" aria-label="Main">
            <Logo size={24} />
            <Link className="lp-btn lp-btn-primary lp-btn-small" to="/login">Sign in</Link>
          </nav>
          <div className="lp-hero-grid">
            <div className="lp-hero-copy">
              <span className="lp-eyebrow">For emergency services and rostered crews</span>
              <h1>Less chasing,<br /><em>more doing.</em></h1>
              <p className="lp-lead">
                MusterTap puts your unit's or brigade's rosters, cover requests and vehicle checks in one place.
                Ask for cover in one tap, see who's on tonight, and find any item on any vehicle.
              </p>
              <div className="lp-actions">
                <Link className="lp-btn lp-btn-primary" to="/login">Sign in</Link>
                <a className="lp-btn lp-btn-ghost" href="#cover">See how it works</a>
              </div>
              <p className="lp-note">Works on phone, tablet and PC. Nothing to install.</p>
            </div>
            <div className="lp-hero-phone">
              <div className="lp-phone">
                <Shot eager src="/landing/whos-on.webp" w={780} h={690}
                  alt="Who's on this week: Sunday night duty crew Blue, Lucy Bennett covered by Max Fischer, 5 on with TRK 1, SUP 3, SWAHS 5" />
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="lp-strip">
        <div className="lp-wrap">
          <p className="lp-label">Three things that eat into every week</p>
          <div>
            <h3><span className="lp-dot" style={{ background: ORANGE }} />Finding cover</h3>
            <p>A pager message through the operator goes one way. Nobody can see whether the gap was ever filled.</p>
          </div>
          <div>
            <h3><span className="lp-dot" style={{ background: YELLOW }} />Knowing who's on</h3>
            <p>Who is on tonight, who is covering whom, and which team is short a driver lives in a few heads and spreadsheets.</p>
          </div>
          <div>
            <h3><span className="lp-dot" style={{ background: BLUE }} />Finding gear</h3>
            <p>Items get moved, faults get mentioned once and forgotten, and new members don't know where things live.</p>
          </div>
        </div>
      </div>

      <main>
        <section className="lp-video" id="video">
          <div className="lp-wrap">
            <span className="lp-kicker">See it in action</span>
            <h2>MusterTap in three minutes</h2>
            <VideoPlayer id={OVERVIEW_VIDEO} title="MusterTap in three minutes" poster="/landing/video-overview.webp" />
            <p className="lp-video-note">Plays from YouTube. Nothing loads from there until you press play.</p>
            <div className="lp-roles">
              <b>Made for your role:</b>
              {ROLE_VIDEOS.map(([label, href]) => (
                <a key={label} className="lp-btn lp-btn-ghost lp-btn-small" href={href} target="_blank" rel="noreferrer">{label}</a>
              ))}
            </div>
          </div>
        </section>

        <section className="lp-feature" id="cover">
          <div className="lp-wrap lp-feature-grid">
            <div className="lp-feature-copy">
              <span className="lp-kicker">Cover</span>
              <h2>Cover in one tap</h2>
              <p className="lp-sub">Asking for cover and offering it takes seconds, not a round of phone calls.</p>
              <Points items={[
                ['Partial cover is welcome', 'Take the whole shift, or just the two hours you can do.'],
                ['The right people are asked', 'Members who are free, not on leave and have the skills hear about it first.'],
                ["Reminders until it's filled", "Team leaders are told before the shift if it's still short."],
                ['Fair share, on show', '"Leo has covered you 2 times this year." Covers given and received are visible to all.'],
              ]} />
            </div>
            <div className="lp-shot">
              <div className="lp-phone full">
                <Shot src="/landing/cover.webp" w={780} h={1380}
                  alt="Cover request with partly covered hours, the crew's skills and an 'I'll cover all of it' button" />
              </div>
            </div>
          </div>
        </section>

        <div className="lp-band">
          <div className="lp-wrap">
            <h2>How a cover request runs</h2>
            <ol className="lp-steps">
              <li><b>Ask</b><span>Tap the shift. Whole shift or just the hours you need.</span></li>
              <li><b>Match</b><span>MusterTap finds who's free, not on leave and qualified.</span></li>
              <li><b>Notify</b><span>A push goes to the best fits first, then wider.</span></li>
              <li><b>Take</b><span>One tap covers it all, or takes a slice.</span></li>
              <li><b>Done</b><span>The roster updates. The UDO and team leader can see it.</span></li>
            </ol>
            <p className="lp-callout">No reply? MusterTap reminds the asker and the pool, then tells the team leader before the shift starts.</p>
          </div>
        </div>

        <section className="lp-feature flip" id="shifts">
          <div className="lp-wrap lp-feature-grid">
            <div className="lp-feature-copy">
              <span className="lp-kicker">Shifts</span>
              <h2>One screen answers "who's on?"</h2>
              <p className="lp-sub">Duty crew, UDO, day UDO and ops support for the week, without sending a single message.</p>
              <Points items={[
                ["Who's rostered and who's covering whom", 'Team leader and deputy marked; swaps shown as Lucy Bennett → Max Fischer.'],
                ['Skills on the crew', 'Counts for drivers, SWAHS and the rest, so a missing truck driver stands out.'],
                ['Leave on every roster', "Members on leave aren't counted in the crew and aren't asked to cover."],
                ['Ops support, fairly shared', "Members claim shifts; leftovers get a suggestion based on each person's load."],
              ]} />
              <div className="lp-chips" aria-label="Example crew summary">
                <span className="lp-chip ok">5 on</span>
                {['TRK 2', 'SUP 1', 'SWAHS 5', 'RLR 4', 'EVR 1', 'C/S 1'].map((c) => <span key={c} className="lp-chip">{c}</span>)}
              </div>
            </div>
            <div className="lp-shot">
              <div className="lp-phone full">
                <Shot src="/landing/ops.webp" w={780} h={1240} alt="Ops support week with suggested members for open shifts" />
              </div>
            </div>
          </div>
        </section>

        <section className="lp-feature" id="gear">
          <div className="lp-wrap lp-feature-grid">
            <div className="lp-feature-copy">
              <span className="lp-kicker">Equipment</span>
              <h2>Find it, fix it, learn it</h2>
              <p className="lp-sub">Photos, locations and fault tracking for every item on every vehicle and shelf.</p>
              <Points items={[
                ['Find it', 'The exact spot, like GR1 › Driver side › Locker 2. Search by name on a job.'],
                ['Faults followed to fixed', 'Found on a check or reported any time — with a photo you can circle and label — a fault gets an owner, notes and a work-order number, and closes itself when the next check finds it fine.'],
                ['Off run at a glance', "When a vehicle is out of service, everyone is told and it shows in red on the “who's on” board until it's back."],
                ['Checks that rotate', "Scheduled truck checks move between teams, and the next check picks up anything not sorted."],
                ['Works in the shed, too', 'No signal in the truck bay? Every tap and photo is saved on the phone and sends when you’re back in range.'],
                ['Learn it', 'New members can browse items with photos and where they live.'],
              ]} />
            </div>
            <div className="lp-shot">
              <div className="lp-phone full">
                <Shot src="/landing/checks.webp" w={780} h={1180}
                  alt="Checks screen: 3 open issues, an active check of General Rescue 1 at 8 of 16 items" />
              </div>
            </div>
          </div>
        </section>

        <section className="lp-feature" id="reports">
          <div className="lp-wrap lp-feature-grid">
            <div className="lp-feature-copy">
              <span className="lp-kicker">For unit leadership</span>
              <h2>The month, in one report</h2>
              <p className="lp-sub">No more pulling numbers together from spreadsheets and message threads before a meeting.</p>
            </div>
            <div className="lp-feature-copy">
              <Points items={[
                ['Readiness', 'Checks done on time, late or missed, faults found and fixed, and days each vehicle was off run.'],
                ['Crews and cover', 'Shifts that were short or missing a key skill, cover asked and filled, and who carried the load.'],
                ['People', 'Who was on leave and which tickets have expired or are about to.'],
                ['Any period, any time', 'This month, a quarter or the financial year — as a PDF, a CSV, or straight to your inbox. Admins also get last month’s on the 1st.'],
              ]} />
            </div>
          </div>
        </section>

        <section className="lp-feature" id="people">
          <div className="lp-wrap">
            <span className="lp-kicker">Looks after your people</span>
            <h2>Reminders that respect their sleep</h2>
            <p className="lp-sub">Volunteers have day jobs and families. MusterTap tells them what they need, when it's useful.</p>
            <div className="lp-who-grid">
              <div className="lp-who-card"><b>Quiet hours</b><span>Each member picks when their phone stays silent — weeknights, weekends, public holidays. Nothing is lost: it's all waiting in the morning, and anything urgent still gets through.</span></div>
              <div className="lp-who-card"><b>Shift reminders</b><span>A nudge the evening before a 6am shift, never in the middle of the night.</span></div>
              <div className="lp-who-card"><b>Roster in their calendar</b><span>Shifts, covers and activities show up in Google, Apple or Outlook and keep themselves up to date.</span></div>
              <div className="lp-who-card"><b>Nothing lapses quietly</b><span>Tickets like EVR, extinguishers and service dates flag themselves 30 days out.</span></div>
            </div>
          </div>
        </section>

        <section className="lp-band lp-who" id="who">
          <div className="lp-wrap">
            <span className="lp-kicker">Who it's for</span>
            <h2>For any crew that runs on rosters and shared gear</h2>
            <p className="lp-sub">
              If your people take turns on shifts, swap and cover for each other, and look after vehicles and
              equipment together, MusterTap fits.
            </p>
            <div className="lp-who-grid">
              <div className="lp-who-card"><b>Emergency services</b><span>SES units, CFA and other volunteer fire brigades, Marine Rescue and Coast Guard, Surf Life Saving patrols</span></div>
              <div className="lp-who-card"><b>First responders and first aid</b><span>Volunteer ambulance and community first responders, event first-aid teams, search and rescue groups</span></div>
              <div className="lp-who-card"><b>On-call crews</b><span>Council depots, utility and maintenance crews, site and mine rescue teams with on-call rosters</span></div>
              <div className="lp-who-card"><b>Community organisations</b><span>Community transport, volunteer groups and clubs that share vehicles, trailers and equipment</span></div>
            </div>
          </div>
        </section>

        <div className="lp-wrap lp-duo">
          <div>
            <h2>Not everyone has to use the app</h2>
            <p className="lp-sub">
              Nobody is forced to install anything. Members without the app still appear on shift boards and count
              towards the crew. Team leaders and deputies can ask for cover, record swaps and enter leave on their
              behalf, and get that member's updates instead.
            </p>
          </div>
          <div>
            <h2>Works on any device</h2>
            <p className="lp-sub">Open the link in a browser, or add it to your home screen so it opens like an app. Checks keep working with no signal and send when you're back in range.</p>
            <div className="lp-devices">
              <div className="lp-device">
                <svg {...DEVICE_ICON}><rect x="7" y="2.5" width="10" height="19" rx="2.2" /><path d="M11 18.5h2" /></svg>
                <b>Phone</b><span>Android and iPhone</span>
              </div>
              <div className="lp-device">
                <svg {...DEVICE_ICON}><rect x="4" y="2.5" width="16" height="19" rx="2.2" /><path d="M11 18.5h2" /></svg>
                <b>Tablet</b><span>iPad or Android</span>
              </div>
              <div className="lp-device">
                <svg {...DEVICE_ICON}><rect x="3" y="4" width="18" height="12" rx="1.6" /><path d="M1.5 19.5h21" /></svg>
                <b>PC or laptop</b><span>Any modern browser</span>
              </div>
            </div>
          </div>
        </div>
      </main>

      <section className="lp-close" id="signin">
        <div className="lp-wrap">
          <div>
            <h2>Already on a team?</h2>
            <p>Sign in with the email your admin invited. New member? Ask your team leader for an invite link.</p>
          </div>
          <Link className="lp-btn lp-btn-primary" to="/login">Sign in to MusterTap</Link>
        </div>
      </section>

      <footer className="lp-footer">
        <div className="lp-wrap">
          <span>MusterTap by KinCue · ABN 75 863 951 924 · <Link to="/privacy">Privacy</Link> · <a href="mailto:enquiries@mustertap.com">enquiries@mustertap.com</a> · <a href="https://www.youtube.com/@mustertap" target="_blank" rel="noreferrer">YouTube</a></span>
          <span>Screens are from a demo unit. All names are made up. Not affiliated with or endorsed by any agency named on this page.</span>
        </div>
      </footer>
    </div>
  );
}
